/* eslint-disable require-atomic-updates */
import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment';
import {HTTPClient} from '../assets/js/http';
import { wlData } from '../assets/js/constants';
import { sitesUserCanAccess } from '../assets/js/siteAccess';
let client = new HTTPClient();

Vue.use(Vuex)

function getCookieByName(name) {
   try {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if(match){
      return decodeURIComponent(match[2])
    } else {
      return null;
    }
   } catch (error) {
     console.error(error);  
   }
}

/**
 * Last explicitly selected site for cross-app nav, or null for All Sites.
 * @returns {string|null}
 */
function readStoredAppSiteId() {
    try {
        return sessionStorage.getItem('hawken_app_site_id') || null;
    } catch (error) {
        return null;
    }
}

/**
 * Persist the cross-app site selection (null = All Sites).
 * @param {string|null} siteId
 */
function writeStoredAppSiteId(siteId) {
    try {
        if (siteId) sessionStorage.setItem('hawken_app_site_id', siteId);
        else sessionStorage.removeItem('hawken_app_site_id');
    } catch (error) {
        /* ignore */
    }
}

export default new Vuex.Store({
    state: {
        logo:  '',
        defaultLogo: true,
        project: {},
        projects: [],
        sites: [],
        site: null,
        /**
         * Site id for tab/breadcrumb nav. Null means All Sites on apps that support it.
         */
        appSiteId: readStoredAppSiteId(),
        theme: window.User?.theme || 'dark',
        themes: window.Project?.themes || null,
        user: window.User,
        kit: window.Kit,
        editMode: false,
        TrendData: {},
        trendDaemon: {},
        PointTrendData: {},
        pointTrendDaemon: {},
        alerts: [],
        alertsCssClass: 'msmt-alarm',
        daemonFetchTime: (new Date()).valueOf(),
        pointDaemonFetchTime: (new Date()).valueOf(),
        dashboards: [],
        currentDashboard: null,
        dashboardFolders: [],
        dashboardLoading: false,
        dashboardError: null,
        zones: {}, // Object keyed by siteId containing arrays of zones
        navigatorVisible: true, // Control navigator visibility
        modalOpen: false // Track modal state
    },
    getters: {
        isLoggedIn(state) {
            if (!state.token) {
                state.token = getCookieByName('authToken')
            }
            return !!state.token
        },
        getUser(state) {
            if (!state.user) {
                let user = localStorage.getItem('user');
                if (user) {
                    state.user = JSON.parse(user);
                }
            }
            return !!state.user;
        },
        authStatus: state => state.status,
        getSite(state) {
            return state.site || null
        },
        /**
         * Sites the current user may open in the current project.
         * @param {object} state
         * @returns {Array}
         */
        accessibleSites(state) {
            const pid = state.project?.objectId || null;
            return sitesUserCanAccess(state.sites, state.user, pid);
        },
        /**
         * First accessible site for site-only nav fallbacks.
         * @param {object} state
         * @param {object} getters
         * @returns {object|null}
         */
        firstAccessibleSite(state, getters) {
            const list = getters.accessibleSites || [];
            return list[0] || null;
        },
        /**
         * True when the current HAQ project has an Atrium org.
         * @param {object} state
         * @returns {boolean}
         */
        hasAtriumOrg(state) {
            const project = state.project;
            if (project && project.hasAtriumOrg) return true;
            const id = project && (project.objectId || project.id);
            if (!id) return false;
            const listed = (state.projects || []).find(function(row) {
                return row && (row.objectId === id || row.id === id) && row.hasAtriumOrg;
            });
            return !!(listed && listed.hasAtriumOrg);
        }
    },
    mutations: {
        setAlerts(state, alerts){
            const list = Array.isArray(alerts) ? alerts.slice() : [];
            /**
             * Alert type from a populated config, or empty.
             * @param {object} a
             * @returns {string}
             */
            const typeOf = (a) => (a && a.alertConfig && a.alertConfig.alertType) || '';
            list.sort(function(a, b){
                const ta = typeOf(a);
                const tb = typeOf(b);
                if (ta === tb) return 0;
                if (ta > tb) return 1;
                return -1;
            });
            let criticalCount = 0;
			let warningCount = 0;
            let infoCount = 0;
			list.forEach(a => {
                const t = typeOf(a);
				if (t === 'critical')
					criticalCount++;
				else if(t === 'warning')
					warningCount++;
                else if(t === 'info')
                    infoCount++;
			});
            let cssClass = 'msmt-alarm';
			if (criticalCount)
				cssClass = 'msmt-alarm msmt-critical';
			else if(warningCount)
				cssClass = 'msmt-alarm msmt-warning';
            else if(infoCount)
                cssClass = 'msmt-alarm msmt-info';
            state.alertsCssClass = cssClass;
            state.alerts = list;
        },
        auth_request(state) {
            state.status = 'loading'
        },
        auth_success(state, token, user) {
            state.status = 'success';
            state.token = token;
            state.user = user;
        },
        auth_error(state) {
            state.status = 'error';
        },
        setSite(state, site) {
            state.site = site
        },
        /**
         * Set the site used when switching apps. Pass null for All Sites.
         * @param {object} state
         * @param {string|null} siteId
         */
        setAppSiteId(state, siteId) {
            const id = siteId || null;
            state.appSiteId = id;
            writeStoredAppSiteId(id);
        },
        setUser(state, user){
            state.user = user;
        },
        logout(state) {
            state.status = null;
            state.token = null;
        },
        setProject(state, project){
            const prevId = state.project && state.project.objectId;
            const nextId = project && project.objectId;
            /**
             * Keep Atrium visibility when a later API payload omits hasAtriumOrg.
             * @param {object} incoming
             * @returns {object}
             */
            function withAtriumFlag(incoming) {
                if (!incoming || incoming.hasAtriumOrg) return incoming;
                const id = incoming.objectId || incoming.id;
                const prev = state.project;
                if (prev && prev.hasAtriumOrg && (prev.objectId === id || prev.id === id)) {
                    return Object.assign({}, incoming, { hasAtriumOrg: true });
                }
                const listed = (state.projects || []).find(function(row) {
                    return row && (row.objectId === id || row.id === id) && row.hasAtriumOrg;
                });
                if (listed) return Object.assign({}, incoming, { hasAtriumOrg: true });
                return incoming;
            }
            project = withAtriumFlag(project);
            state.project = project;
            if (prevId && nextId && prevId !== nextId) {
                state.appSiteId = null;
                writeStoredAppSiteId(null);
            }
        },
        setProjects(state, projects){
            if(projects){
                projects.sort(function(a,b){
                    if(a.name < b.name)
                        return -1;
                    return 1;
                });
            }
            state.projects = projects;
        },
        setThemes(state, themes) {
            state.themes = themes
        },
        setTheme(state, theme){
            state.theme = theme
        },
        setLogo(state,logo){
            let theme = state.theme ? state.theme : 'dark';
            if (logo) {
                state.logo = logo;
                state.defaultLogo = false;
            }
            else if (state.project?.whitelabel === "CommanderAQ") {
                state.logo = `/images/logo/${theme}/commander.png`;
            }
            else {
                if(theme == 'light'){
                    state.logo = wlData.lightCornerLogo||`/images/logo/light/hawken.png`;
                }else{
                    state.logo = wlData.darkCornerLogo||`/images/logo/dark/hawken.png`;
                }
            }
        },
        setSites(state, sites){
            state.sites = sites;
        },
        setTrendData(state, {options, trendData}){
            if(state.TrendData[options.pointId]){
                let cache = state.TrendData[options.pointId]
                // if(options.start < cache.start){ // always update the cache if setTrendData is called
                    Object.assign(state.TrendData[options.pointId], trendData);
                    state.TrendData[options.pointId].data = state.TrendData[options.pointId].data.map((el) => {
                        if(!isNaN(Number(el[1]))){
                            return [el[0], Math.round(el[1] * 100)/100]
                        } else {
                            return el
                        }
                    })
                    state.TrendData[options.pointId].start = options.start;
                    state.TrendData[options.pointId].startString = moment(options.start).format('hh:mm:ss a DD dddd MMM YYYY')
                // }
            } else {
                state.TrendData[options.pointId] = new Object();
                state.TrendData[options.pointId] = trendData;
                state.TrendData[options.pointId].data = state.TrendData[options.pointId].data.map((el) => {
                    if(!isNaN(Number(el[1]))){
                        return [el[0], Math.round(el[1] * 100)/100]
                    } else {
                        return el
                    }
                })
                state.TrendData[options.pointId].start = options.start;
                if(options.type){
                    if(['scoreAQ', 'scoreOW', 'scoreOA'].includes(options.type)){
                        state.TrendData[options.pointId].field = "curScore"
                    } else {
                        state.TrendData[options.pointId].field = "curVal"
                    }
                }
                state.TrendData[options.pointId].startString = moment(options.start).format('hh:mm:ss a DD dddd MMM YYYY')
            }
        },
        setLatestTrendData(state, {pointId, curVal, curScore}){
            if(state.TrendData[pointId]){
                let field = state.TrendData[pointId].field;
                let lastTimeStamp = state.TrendData[pointId].data[state.TrendData[pointId].data.length - 1]?.[0] || 0;
                // console.log(lastTimeStamp)
                // console.log(new Date(lastTimeStamp).toString());
                let lastestNewVal = field == "curVal" ? curVal : curScore;
                if(lastestNewVal.ts > lastTimeStamp){
                    // console.log('Pushing to trendData ', lastestNewVal)
                    state.TrendData[pointId].data.push([lastestNewVal.ts, !isNaN(lastestNewVal.val) ? Math.round(lastestNewVal.val * 100)/100 : lastestNewVal.val])
                }
            }
        },
        setPointTrendData(state, {options, trendData}){
            if(state.PointTrendData[options.pointId]){
                let cache = state.PointTrendData[options.pointId]
                // if(options.start < cache.start){ // always update the cache if setPointTrendData is called
                    Object.assign(state.PointTrendData[options.pointId], trendData);
                    state.PointTrendData[options.pointId].data = state.PointTrendData[options.pointId].data.map((el) => {
                        if(!isNaN(Number(el[1]))){
                            return [el[0], Math.round(el[1] * 100)/100]
                        } else {
                            return el
                        }
                    })
                    state.PointTrendData[options.pointId].start = options.start;
                    
                    // Ensure curVal and curScore properties are updated from the API response
                    if(trendData.curVal !== undefined) {
                        state.PointTrendData[options.pointId].curVal = trendData.curVal;
                    }
                    if(trendData.curScore !== undefined) {
                        state.PointTrendData[options.pointId].curScore = trendData.curScore;
                    }
                    
                    // Store point metadata for icon display
                    if(trendData.type !== undefined) {
                        state.PointTrendData[options.pointId].type = trendData.type;
                    }
                    if(trendData.pointType !== undefined) {
                        state.PointTrendData[options.pointId].pointType = trendData.pointType;
                    }
                    if(trendData.unit !== undefined) {
                        state.PointTrendData[options.pointId].unit = trendData.unit;
                    }
                    if(trendData.kind !== undefined) {
                        state.PointTrendData[options.pointId].kind = trendData.kind;
                    }
                    
                    state.PointTrendData[options.pointId].startString = moment(options.start).format('hh:mm:ss a DD dddd MMM YYYY')
                // }
            } else {
                state.PointTrendData[options.pointId] = new Object();
                state.PointTrendData[options.pointId] = trendData;
                state.PointTrendData[options.pointId].data = state.PointTrendData[options.pointId].data.map((el) => {
                    if(!isNaN(Number(el[1]))){
                        return [el[0], Math.round(el[1] * 100)/100]
                    } else {
                        return el
                    }
                })
                state.PointTrendData[options.pointId].start = options.start;
                if(options.type){
                    if(['scoreAQ', 'scoreOW', 'scoreOA'].includes(options.type)){
                        state.PointTrendData[options.pointId].field = "curScore"
                    } else {
                        state.PointTrendData[options.pointId].field = "curVal"
                    }
                }
                
                // Ensure curVal and curScore properties are set from the API response
                if(trendData.curVal !== undefined) {
                    state.PointTrendData[options.pointId].curVal = trendData.curVal;
                }
                if(trendData.curScore !== undefined) {
                    state.PointTrendData[options.pointId].curScore = trendData.curScore;
                }
                
                // Store point metadata for icon display
                if(trendData.type !== undefined) {
                    state.PointTrendData[options.pointId].type = trendData.type;
                }
                if(trendData.pointType !== undefined) {
                    state.PointTrendData[options.pointId].pointType = trendData.pointType;
                }
                if(trendData.unit !== undefined) {
                    state.PointTrendData[options.pointId].unit = trendData.unit;
                }
                if(trendData.kind !== undefined) {
                    state.PointTrendData[options.pointId].kind = trendData.kind;
                }
                
                state.PointTrendData[options.pointId].startString = moment(options.start).format('hh:mm:ss a DD dddd MMM YYYY')
            }
        },
        setLatestPointTrendData(state, {pointId, curVal, curScore}){
            if(state.PointTrendData[pointId]){
                let field = state.PointTrendData[pointId].field;
                let lastTimeStamp = state.PointTrendData[pointId].data[state.PointTrendData[pointId].data.length - 1]?.[0] || 0;
                let lastestNewVal = field == "curVal" ? curVal : curScore;
                if(lastestNewVal.ts > lastTimeStamp){
                    state.PointTrendData[pointId].data.push([lastestNewVal.ts, !isNaN(lastestNewVal.val) ? Math.round(lastestNewVal.val * 100)/100 : lastestNewVal.val])
                }
                
                // Update the curVal and curScore properties for color logic
                if(curVal && curVal.val !== undefined) {
                    state.PointTrendData[pointId].curVal = curVal.val;
                }
                if(curScore && curScore.val !== undefined) {
                    state.PointTrendData[pointId].curScore = curScore.val;
                }
            }
        },
        setUserNotifications(state, notifications){
            state.user.notifications = notifications;
        },
        toggleEditMode(state){
            state.editMode = !state.editMode;
        },
        // Dashboard mutations
        setDashboards(state, dashboards) {
            state.dashboards = dashboards;
        },
        /**
         * Patch one dashboard in the list without a refetch.
         * @param {object} state
         * @param {{ dashboardId: string, updateData: object }} payload
         */
        patchDashboardInList(state, { dashboardId, updateData }) {
            const list = state.dashboards || [];
            state.dashboards = list.map(row => {
                if (!row || row.objectId !== dashboardId) return row;
                return { ...row, ...(updateData || {}) };
            });
        },
        setCurrentDashboard(state, dashboard) {
            state.currentDashboard = dashboard;
        },
        setDashboardLoading(state, loading) {
            state.dashboardLoading = loading;
        },
        setDashboardError(state, error) {
            state.dashboardError = error;
        },
        /**
         * Replace the project dashboard folder list.
         * @param {object} state
         * @param {Array} folders
         */
        setDashboardFolders(state, folders) {
            state.dashboardFolders = folders || [];
        },
        setZones(state, { siteId, zones }) {
            Vue.set(state.zones, siteId, zones);
        },
        setNavigatorVisible(state, visible) {
            state.navigatorVisible = visible;
        },
        setModalOpen(state, open) {
            state.modalOpen = open;
        }
    },
    actions: {
        async fetchAlerts({commit}, siteId){
            if(!siteId) return;
            let results = await client.robustQuery(`/api/sites/${siteId}/alerts?isActive=true`);
            if(results && results.alerts){
                commit('setAlerts', results.alerts);
            }
        },
        setDefaultTheme({commit}, theme) {
            commit('setTheme',theme);
            commit('setLogo');
        },
        setCustomTheme({commit, state}, [usertheme, themes]) {
            try {
                if(!themes) return;
                let theme = themes[usertheme ? usertheme : state.theme];
                if(!theme) return;
                commit('setTheme',usertheme ? usertheme : state.theme);
                commit('setThemes',theme);
                if(theme.logo)
                    commit('setLogo', theme.logo);
                else
                    commit('setLogo');

                let vapp = document.querySelectorAll('.v-application')[0];
                if (vapp) {
                    vapp.style.setProperty('--app-primary-bg', theme.bg1)
                    vapp.style.setProperty('--app-secondary-bg', theme.bg3)
                    vapp.style.setProperty('--app-tertiary-bg', theme.bg3)

                    vapp.style.setProperty('--app-primary-txt', theme.txt1)
                    vapp.style.setProperty('--app-secondary-txt', theme.txt2)
                    vapp.style.setProperty('--app-tertiary-txt', theme.txt3)

                    // vapp.style.setProperty('--navbar-txt', theme.)
                    // vapp.style.setProperty('--navbar-link-active', theme.)
                    // vapp.style.setProperty('--navbar-link-inactive', theme.)
                    // vapp.style.setProperty('--navbar-theme-toggle', theme.)

                    // vapp.style.setProperty('--btn-primary-bg', theme.)
                    // vapp.style.setProperty('--btn-primary-border', theme.)
                    // vapp.style.setProperty('--btn-primary-txt', theme.)

                    // vapp.style.setProperty('--btn-secondary-bg', theme.)
                    // vapp.style.setProperty('--btn-secondary-border', theme.)
                    // vapp.style.setProperty('--btn-secondary-txt', theme.)
                    
                    vapp.style.setProperty('--input-bg', theme.fbg1)
                    vapp.style.setProperty('--input-txt', theme.ftxt1)

                    // vapp.style.setProperty('--bg-tooltip', theme.)

                    vapp.style.setProperty('--card-primary-bg', theme.bg2)
                    // vapp.style.setProperty('--card-secondary-bg', theme.)
                    // vapp.style.setProperty('--card-button-bg', theme.)
                    // vapp.style.setProperty('--card-button-deactivated-bg', theme.)
                    // vapp.style.setProperty('--card-button-deactivated-txt', theme.)

                    vapp.style.setProperty('--kiosk-bg', theme.bg1)
                    // vapp.style.setProperty('--kiosk-card-bg', theme.)

                    // vapp.style.setProperty('--table-check-bg', theme.)
                    // vapp.style.setProperty('--table-selected-bg', theme.)
                    // vapp.style.setProperty('--table-highlight-bg', theme.)

                    // vapp.style.setProperty('--border', theme.)
                    // vapp.style.setProperty('--box-shadow', theme.)

                    // vapp.style.setProperty('--primary-alert', theme.)

                    // vapp.style.setProperty('--score-blue-color', theme.)
                    // vapp.style.setProperty('--score-yellow-color', theme.)
                    // vapp.style.setProperty('--score-red-color', theme.)

                    vapp.style.setProperty('--airquality', theme.aq1)
                    vapp.style.setProperty('--co', theme.co1)
                    vapp.style.setProperty('--rh', theme.rh1)
                    vapp.style.setProperty('--plt', theme.plt1)
                    vapp.style.setProperty('--vt', theme.vt1)
                    vapp.style.setProperty('--spt', theme.spt1)

                    vapp.style.setProperty('--airquality2', theme.aq2)
                    vapp.style.setProperty('--co2', theme.co2)
                    vapp.style.setProperty('--rh2', theme.rh2)
                    vapp.style.setProperty('--plt2', theme.plt2)
                    vapp.style.setProperty('--vt2', theme.vt2)
                    vapp.style.setProperty('--spt2', theme.spt2)
                }
            } catch (error) {
                console.error(error)
            }
        },
        async login({commit}, user) {
            try {
                let res = await client.robustPost('/users/login', user);
                commit('auth_success', res.token, res.user)
            } catch (error) {
                commit('auth_error')
                window.alert('Incorrect Username and Password');
                throw new Error(error);
            }
        },
        async loginWithToken({commit}, object) {
            try {
                commit('auth_success', object.token, object.user)
            } catch (error) {
                commit('auth_error');
                console.log(error);
                window.alert('Invalid token');
                throw new Error(error);
            }
        },
        async logout({commit}) {
            try {
                window.location = "/logout";
            } catch (error) {
                commit('auth_error')
                throw new Error(error)
            }
        },
        async updateToken({commit}, data) {
            try {
                let res = await client.robustPost('/users/updatetoken', data);
                commit('auth_success', res.token, res.user);
            } catch (error) {
                commit('auth_error')
                console.log(error);
                throw new Error(error);
            }
        },
        async loadLocals({commit}){
            try {
                let res = await client.robustQuery('/locals');
                commit('setUser', res.user);
                commit('setProject', res.project);

                this.state.kit = res.kit;
                this.state.devices = res.devices;
                
                if(Array.isArray(res.projects))
                    commit('setProjects', res.projects)

                // Apply theme after locals load (webpack/dev has no window.User/Project at boot).
                let theme = (res.user && res.user.theme) || 'dark';
                if (res.project && res.project.themes != null)
                    await this.dispatch('setCustomTheme', [theme, res.project.themes]);
                else
                    await this.dispatch('setDefaultTheme', theme);

                window.User = res.user;
                window.Project = res.project;
                window.Projects = res.projects;
                window.Kit = res.kit;
                window.Devices = res.devices;
                return res;
            } catch (error) {
                console.error(error);
            }
        },
        localsThroughWindow({commit}){
            console.warn("Loading locals through window")
            // commit('setUser', window.User);
            commit('setProject', window.Project);

            this.state.kit = window.Kit;
            this.state.devices = window.Devices;
            
            if(Array.isArray(window.Projects))
                commit('setProjects', window.Projects)
            
            // Setting Theme
            // let theme = window.User.theme || 'dark'; 
            // if(window.Project.themes!= null)
            //     this.dispatch('setCustomTheme', [theme, window.Project.themes])
            // else
            //     this.dispatch('setDefaultTheme', theme);
        },
        async getSites({state, commit}){
            try {
                if(state.sites.length == 0){
                    let res = await client.robustQuery('/api', {slim: true});
                    const pid = state.project?.objectId || res.project?.objectId || null;
                    commit('setSites', sitesUserCanAccess(res.sites, state.user, pid));
                }
                const pid = state.project?.objectId || null;
                return sitesUserCanAccess(state.sites, state.user, pid);
            } catch (error) {
                console.error(error);
            }
        },
        async getTrendData({commit, state, dispatch}, options={}){
            try {
                if(!options.force && state.TrendData[options.pointId]!= null && options.startTime>= state.TrendData[options.pointId].start){
                    // console.log('Fetching cached data');
                    let chartData = Object.assign({}, state.TrendData[options.pointId])
                    // console.log(chartData.data.length)
                    if(options.endTime)
                        chartData.data = chartData.data.filter((el) => el[0]< options.endTime);
                    chartData.data = chartData.data.filter((el) => el[0]>= options.startTime);
                    // console.log(chartData.data.length)
                    return chartData
                }
                let chartData = await client.robustQuery('/api/trendData', {
                    siteId: options.siteId,
                    pointId: options.pointId,
                    version: 'highcharts',
                    startTime: options.startTime,
                    type: options.type
                });
                let subOptions = {pointId: options.pointId, start: options.startTime, type: options.type};
                // console.log(options.type, moment(chartData.results.data[0][0] || null).format('hh:mm:ss a DD dddd MMM YYYY'))
                commit('setTrendData', {options: subOptions, trendData: chartData.results})
                if(state.trendDaemon[options.siteId] == null){
                    setInterval(dispatch, state.site?.uiCurvalUpdateInterval || 1000 * 60 * 10, 'trendDaemonRunner', options.siteId) // every 10 minutes
                    state.trendDaemon[options.siteId] = true;
                }
                return state.TrendData[options.pointId];
            } catch (error) {
                console.error(error);
            }
        },
        async trendDaemonRunner({state, commit}, siteId){
            try {
                // console.log('Kicking on trendDaemon Runner for ' + siteId)
                let measurementIds = JSON.stringify(Object.keys(state.TrendData))
                let latestValues = await client.robustQuery(`/api/site/${siteId}/latest`, {measurementIds});
                let updated = false;
                for(let i in latestValues.results){
                    if(latestValues.results[i].curVal || latestValues.results[i].curScore){
                        commit('setLatestTrendData', {curVal: latestValues.results[i].curVal, curScore: latestValues.results[i].curScore, pointId: i})
                        updated = true 
                    }
                }
                if(updated){
                    state.daemonFetchTime = (new Date()).valueOf()
                }
            } catch (error) {
                console.error(error);
            }
        },
        async getPointTrendData({commit, state, dispatch}, options={}){
            try {
                // Check if required parameters are provided
                if (!options.siteId) {
                    console.error('getPointTrendData: siteId is required');
                    return null;
                }
                if (!options.pointId) {
                    console.error('getPointTrendData: pointId is required');
                    return null;
                }
                
                if(!options.force && state.PointTrendData[options.pointId]!= null && options.startTime>= state.PointTrendData[options.pointId].start){
                    // console.log('Fetching cached point data');
                    let chartData = Object.assign({}, state.PointTrendData[options.pointId])
                    if(options.endTime)
                        chartData.data = chartData.data.filter((el) => el[0]< options.endTime);
                    chartData.data = chartData.data.filter((el) => el[0]>= options.startTime);
                    return chartData
                }
                let chartData = await client.robustQuery('/api/points/trendData', {
                    siteId: options.siteId,
                    pointId: options.pointId,
                    version: 'highcharts',
                    startTime: options.startTime,
                    type: options.type
                });
                let subOptions = {pointId: options.pointId, start: options.startTime, type: options.type};
                commit('setPointTrendData', {options: subOptions, trendData: chartData})
                if(state.pointTrendDaemon[options.siteId] == null){
                    setInterval(dispatch, state.site?.uiCurvalUpdateInterval || 1000 * 60 * 10, 'pointTrendDaemonRunner', options.siteId) // every 10 minutes
                    state.pointTrendDaemon[options.siteId] = true;
                }
                return state.PointTrendData[options.pointId];
            } catch (error) {
                console.error(error);
                return null;
            }
        },
        async pointTrendDaemonRunner({state, commit}, siteId){
            try {
                // console.log('Kicking on pointTrendDaemon Runner for ' + siteId)
                let pointIds = JSON.stringify(Object.keys(state.PointTrendData))
                let latestValues = await client.robustQuery(`/api/site/${siteId}/latest`, {measurementIds: pointIds});
                let updated = false;
                for(let i in latestValues.results){
                    if(latestValues.results[i].curVal || latestValues.results[i].curScore){
                        commit('setLatestPointTrendData', {curVal: latestValues.results[i].curVal, curScore: latestValues.results[i].curScore, pointId: i})
                        updated = true 
                    }
                }
                if(updated){
                    state.pointDaemonFetchTime = (new Date()).valueOf()
                }
            } catch (error) {
                console.error(error);
            }
        },
        // Dashboard actions
        async getDashboards({ commit, state }, projectId) {
            try {
                commit('setDashboardLoading', true);
                const result = await client.robustQuery('/api/dashboards', { projectId });
                commit('setDashboards', result.results || []);
                return result;
            } catch (error) {
                commit('setDashboardError', error.message);
                throw error;
            } finally {
                commit('setDashboardLoading', false);
            }
        },
        
        async getDashboard({ commit, state }, dashboardId) {
            try {
                commit('setDashboardLoading', true);
                const result = await client.robustQuery(`/api/dashboards/${dashboardId}`);
                commit('setCurrentDashboard', result.results);
                return result;
            } catch (error) {
                commit('setDashboardError', error.message);
                throw error;
            } finally {
                commit('setDashboardLoading', false);
            }
        },
        
        async createDashboard({ commit, state }, dashboardData) {
            console.log('createDashboard', dashboardData);
            try {
                commit('setDashboardLoading', true);
                const result = await client.robustPost('/api/dashboards/new', dashboardData);
                const created = result && result.results;
                if (created && created.objectId) {
                    const row = Object.assign({}, created);
                    if (row.schemaVersion == null) row.schemaVersion = 2;
                    const list = state.dashboards || [];
                    if (!list.some(d => d.objectId === row.objectId)) {
                        commit('setDashboards', [row].concat(list));
                    }
                }
                return result;
            } catch (error) {
                commit('setDashboardError', error.message);
                throw error;
            } finally {
                commit('setDashboardLoading', false);
            }
        },
        
        async updateDashboard({ commit }, { dashboardId, updateData }) {
            try {
                const result = await client.robustPost(`/api/dashboards/${dashboardId}`, updateData);
                if (result && result.results) {
                    commit('setCurrentDashboard', result.results);
                }
                return result;
            } catch (error) {
                commit('setDashboardError', error.message);
                throw error;
            }
        },

        async deleteDashboard({ commit }, dashboardId) {
            try {
                return await client.robustDelete(`/api/dashboards/${dashboardId}`);
            } catch (error) {
                commit('setDashboardError', error.message);
                throw error;
            }
        },

        /**
         * Delete dashboards that are not schema v2.
         * @param {object} ctx
         * @param {{ projectId?: string, keepId?: string }} payload
         */
        async purgeLegacyDashboards({ dispatch, state }, payload) {
            const opts = payload || {};
            const keepId = opts.keepId || null;
            const old = (state.dashboards || []).filter(d => Number(d.schemaVersion) !== 2 && d.objectId !== keepId);
            for (let i = 0; i < old.length; i++) {
                try {
                    await dispatch('deleteDashboard', old[i].objectId);
                } catch (error) {
                    console.error('Failed to delete legacy dashboard', old[i].objectId, error);
                }
            }
            if (old.length && opts.projectId) {
                await dispatch('getDashboards', opts.projectId);
            }
        },

        /**
         * Load folders for a project.
         * @param {object} ctx
         * @param {string} projectId
         */
        async getDashboardFolders({ commit }, projectId) {
            const result = await client.robustQuery('/api/dashboards/folders', { projectId });
            commit('setDashboardFolders', result.results || []);
            return result;
        },

        /**
         * Create a dashboard folder.
         * @param {object} ctx
         * @param {object} folderData
         */
        async createDashboardFolder({ dispatch }, folderData) {
            const result = await client.robustPost('/api/dashboards/folders', folderData);
            if (folderData.project) {
                await dispatch('getDashboardFolders', folderData.project);
            }
            return result;
        },

        /**
         * Update a dashboard folder.
         * @param {object} ctx
         * @param {{ folderId: string, updateData: object, projectId: string }} payload
         */
        async updateDashboardFolder({ dispatch }, { folderId, updateData, projectId }) {
            const result = await client.robustPost(`/api/dashboards/folders/${folderId}`, updateData);
            if (projectId) {
                await dispatch('getDashboardFolders', projectId);
            }
            return result;
        },

        /**
         * Delete a folder and refresh lists.
         * @param {object} ctx
         * @param {{ folderId: string, projectId: string }} payload
         */
        async deleteDashboardFolder({ dispatch }, { folderId, projectId }) {
            const result = await client.robustDelete(`/api/dashboards/folders/${folderId}`);
            if (projectId) {
                await dispatch('getDashboardFolders', projectId);
                await dispatch('getDashboards', projectId);
            }
            return result;
        },
        async getZonesForSite({ commit }, siteId) {
            try {
                const result = await client.robustQuery('/api/zones', { siteId });
                commit('setZones', { siteId, zones: result.results || [] });
                return result.results;
            } catch (error) {
                console.error('Failed to fetch zones:', error);
                return [];
            }
        }
    },
    modules: {}
});